import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createSession,
  deleteSession,
  listMessages,
  listSessions,
  renameSession,
  streamMessage,
  updateSession,
  updateSessionBrandSelection,
  type ChatMessage,
  type ChatSession,
  type ChatSessionCreate,
  type ChatSessionBrandSelection,
  type ChatSessionUpdate,
  type StreamEvent,
} from './chat-api'
import {
  streamSkillInvocation,
  type SkillStreamEvent,
} from '@/features/skills/skills-api'
import { skillKeys } from '@/features/skills/use-skills'
import { useStreamingStore } from './streaming-store'
import type { AttachmentRef } from './attachments-api'

export const chatKeys = {
  all: ['chat'] as const,
  sessions: () => [...chatKeys.all, 'sessions'] as const,
  messages: (sessionId: string) => [...chatKeys.all, 'messages', sessionId] as const,
}

export function useChatSessions() {
  return useQuery({
    queryKey: chatKeys.sessions(),
    queryFn: () => listSessions(),
    staleTime: 15_000,
  })
}

export function useCreateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChatSessionCreate = {}) => createSession(body),
    onSuccess: (session) => {
      qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) => {
        if (!prev) return { sessions: [session] }
        return { sessions: [session, ...prev.sessions] }
      })
    },
  })
}

export function useRenameSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      renameSession(sessionId, title),
    onSuccess: (updated) => {
      qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) => {
        if (!prev) return prev
        return {
          sessions: prev.sessions.map((s) => (s.id === updated.id ? updated : s)),
        }
      })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen')
    },
  })
}

export function useUpdateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, patch }: { sessionId: string; patch: ChatSessionUpdate }) =>
      updateSession(sessionId, patch),
    onSuccess: (updated) => {
      qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) => {
        if (!prev) return prev
        return {
          sessions: prev.sessions.map((s) => (s.id === updated.id ? updated : s)),
        }
      })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Session konnte nicht aktualisiert werden')
    },
  })
}

export function useUpdateSessionBrandSelection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      sessionId,
      selection,
    }: {
      sessionId: string
      selection: ChatSessionBrandSelection
    }) => updateSessionBrandSelection(sessionId, selection),
    onSuccess: (updated) => {
      qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) => {
        if (!prev) return prev
        return {
          sessions: prev.sessions.map((s) => (s.id === updated.id ? updated : s)),
        }
      })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Brand-Auswahl konnte nicht gespeichert werden')
    },
  })
}

export function useDeleteSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) => deleteSession(sessionId),
    onSuccess: (_void, sessionId) => {
      qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) =>
        prev ? { sessions: prev.sessions.filter((s) => s.id !== sessionId) } : prev,
      )
      qc.removeQueries({ queryKey: chatKeys.messages(sessionId) })
      useStreamingStore.getState().reset()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Löschen fehlgeschlagen')
    },
  })
}

export function useChatMessages(sessionId: string | null) {
  return useQuery({
    queryKey: chatKeys.messages(sessionId ?? ''),
    queryFn: () => listMessages(sessionId!),
    enabled: !!sessionId,
    staleTime: 5_000,
  })
}

export interface SkillInvocation {
  skillName: string
  operation?: string
  args?: string
}

export interface FailedSend {
  sessionId: string
  content: string
  attachments?: AttachmentRef[]
  options?: { searchVault?: boolean }
  message: string
}

interface UseSendMessageReturn {
  send: (content: string, attachments?: AttachmentRef[], options?: { searchVault?: boolean }) => Promise<void>
  invokeSkill: (invocation: SkillInvocation) => Promise<void>
  isStreaming: boolean
  streamingContent: string
  /** M5-A: letzter fehlgeschlagener Sendeversuch — Inline-Fehler + Retry im Chat. */
  failedSend: FailedSend | null
  retryFailedSend: () => Promise<void>
  dismissFailedSend: () => void
}

export function useSendMessage(sessionId: string | null): UseSendMessageReturn {
  const qc = useQueryClient()
  const abortRef = useRef<AbortController | null>(null)
  const store = useStreamingStore()
  const [failedSend, setFailedSend] = useState<FailedSend | null>(null)

  // Session-Wechsel leert den Inline-Fehler (M5-A).
  useEffect(() => {
    setFailedSend(null)
  }, [sessionId])

  // Wenn wir in eine laufende Session zurueckkehren, deren Stream noch im
  // globalen Store als aktiv markiert ist, aber lokal kein AbortController
  // haelt, pollern wir kurz die Messages, bis das finale Ergebnis ankommt.
  useEffect(() => {
    if (!sessionId) return
    if (!store.isStreaming || store.sessionId !== sessionId) return
    if (abortRef.current) return

    let cancelled = false
    const poll = async () => {
      for (let i = 0; i < 60; i++) {
        if (cancelled) return
        await new Promise((r) => setTimeout(r, 1000))
        const state = useStreamingStore.getState()
        if (!state.isStreaming || state.sessionId !== sessionId) return
        await qc.invalidateQueries({ queryKey: chatKeys.messages(sessionId) })
      }
      // Timeout: Stream als beendet betrachten, damit UI nicht endlos haengt.
      useStreamingStore.getState().stopStream()
    }
    void poll()
    return () => {
      cancelled = true
    }
  }, [sessionId, store.isStreaming, store.sessionId, qc])

  const upsertMessage = useCallback(
    (message: ChatMessage) => {
      qc.setQueryData<{ messages: ChatMessage[] }>(
        chatKeys.messages(message.session_id),
        (prev) => {
          const list = prev?.messages ?? []
          if (list.some((m) => m.id === message.id)) return prev
          return { messages: [...list, message] }
        },
      )
    },
    [qc],
  )

  const send = useCallback(
    async (content: string, attachments?: AttachmentRef[], options?: { searchVault?: boolean }) => {
      if (!sessionId || store.isStreaming) return
      setFailedSend(null)

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      useStreamingStore.getState().startStream(sessionId, null)

      try {
        await streamMessage({
          sessionId,
          content,
          attachments,
          searchVault: options?.searchVault,
          signal: controller.signal,
          onEvent: (event: StreamEvent) => {
            switch (event.type) {
              case 'user_message': {
                upsertMessage(event.message)
                break
              }
              case 'session_update': {
                qc.setQueryData<{ sessions: ChatSession[] }>(chatKeys.sessions(), (prev) => {
                  if (!prev) return prev
                  return {
                    sessions: prev.sessions.map((s) =>
                      s.id === event.id ? { ...s, title: event.title } : s,
                    ),
                  }
                })
                break
              }
              case 'token': {
                useStreamingStore.getState().appendToken(event.content)
                break
              }
              case 'done': {
                upsertMessage(event.message)
                void qc.invalidateQueries({ queryKey: chatKeys.sessions() })
                break
              }
              case 'error': {
                toast.error(event.message || 'Streaming-Fehler')
                useStreamingStore.getState().setStreamError(event.message || 'Streaming-Fehler')
                break
              }
            }
          },
        })
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Senden fehlgeschlagen'
        // M5-A: Inline-Fehler an der fehlgeschlagenen Nachricht (mit Retry)
        // statt nur Toast.
        setFailedSend({ sessionId, content, attachments, options, message })
        useStreamingStore.getState().setStreamError(message)
      } finally {
        abortRef.current = null
        useStreamingStore.getState().stopStream()
      }
    },
    [sessionId, store.isStreaming, qc, upsertMessage],
  )

  const retryFailedSend = useCallback(async () => {
    if (!failedSend) return
    await send(failedSend.content, failedSend.attachments, failedSend.options)
  }, [failedSend, send])

  const dismissFailedSend = useCallback(() => {
    setFailedSend(null)
  }, [])

  const invokeSkill = useCallback(
    async ({ skillName, operation, args }: SkillInvocation) => {
      if (!sessionId || store.isStreaming) return

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      useStreamingStore.getState().startStream(sessionId, skillName)

      try {
        await streamSkillInvocation({
          sessionId,
          skillName,
          operation,
          args,
          signal: controller.signal,
          onEvent: (event: SkillStreamEvent) => {
            switch (event.type) {
              case 'user_message':
                upsertMessage(event.message)
                break
              case 'skill_invoked':
                if (event.stub) {
                  toast.info(`Skill "${event.skill_name}" läuft mit Standard-Verhalten (keine eigene Anleitung gefunden).`)
                }
                break
              case 'token':
                useStreamingStore.getState().appendToken(event.content)
                break
              case 'done':
                upsertMessage(event.message)
                void qc.invalidateQueries({ queryKey: chatKeys.sessions() })
                void qc.invalidateQueries({ queryKey: skillKeys.invocations(sessionId) })
                break
              case 'error':
                toast.error(event.message || 'Skill-Fehler')
                useStreamingStore.getState().setStreamError(event.message || 'Skill-Fehler')
                break
            }
          },
        })
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Skill-Aufruf fehlgeschlagen'
        toast.error(message)
        useStreamingStore.getState().setStreamError(message)
      } finally {
        abortRef.current = null
        useStreamingStore.getState().stopStream()
      }
    },
    [sessionId, store.isStreaming, qc, upsertMessage],
  )

  return {
    send,
    invokeSkill,
    isStreaming: store.isStreaming && store.sessionId === sessionId,
    streamingContent: store.sessionId === sessionId ? store.streamingContent : '',
    failedSend,
    retryFailedSend,
    dismissFailedSend,
  }
}
